import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import './Help.css';

const FAQ_SECTIONS = [
  {
    title: 'Getting Started',
    icon: '🚀',
    items: [
      {
        q: 'What is EchoMemoir?',
        a: 'EchoMemoir is a family story preservation platform. Record stories from your loved ones, enhance them with AI, and preserve them for future generations. Think of it as a living family archive — voices, stories, and memories all in one place.'
      },
      {
        q: 'How do I create a Family Circle?',
        a: 'After logging in, click "Create Family Circle" on your dashboard. Give it a name (like "The Smith Family"), and you\'re ready to go. You can then invite family members to join and start recording stories together.'
      },
      {
        q: 'How do I invite family members?',
        a: 'Go to your Family Circle → Members tab → click "Invite Member." Enter their email address and choose their role (Storyteller, Contributor, or Listener). They\'ll receive an email invitation to join.'
      },
      {
        q: 'What are the different member roles?',
        a: 'There are four roles:\n\n• Vault Keeper — Full admin access. Can manage members, edit the family tree, and control all settings.\n• Storyteller — Can record stories, add photos, and comment on stories.\n• Contributor — Can add comments and view stories. Can invite others if enabled.\n• Listener — View-only access to stories shared with them.'
      }
    ]
  },
  {
    title: 'Recording Stories',
    icon: '🎙️',
    items: [
      {
        q: 'How do I record a story?',
        a: 'Go to your Family Circle → Stories tab → click "Record a Story." You can choose a story prompt for inspiration or freestyle. Click the record button, speak naturally, and click stop when done. Your recording is automatically transcribed.'
      },
      {
        q: 'What are Story Prompts?',
        a: 'Story Prompts are conversation starters designed to help unlock memories. They cover topics like childhood, school, career, family traditions, and more. Browse the prompt library when creating a new story for inspiration.'
      },
      {
        q: 'Can I upload an existing recording?',
        a: 'Currently, stories are recorded directly in the app. This ensures the best quality for transcription and AI enhancement. We\'re working on audio upload support for a future release.'
      },
      {
        q: 'How long can a recording be?',
        a: 'There\'s no strict time limit, but we recommend keeping stories between 2-15 minutes for the best experience. Longer recordings work fine but may take longer to process.'
      }
    ]
  },
  {
    title: 'AI Features',
    icon: '✨',
    items: [
      {
        q: 'What does "Enhance with AI" do?',
        a: 'AI enhancement takes your raw transcript and creates polished versions:\n\n• Polished — Cleans up grammar, removes filler words, and restructures for clarity.\n• Literary — Transforms the story into a beautifully written narrative while preserving the storyteller\'s voice and meaning.'
      },
      {
        q: 'What is AI Voice Narration?',
        a: 'Voice narration uses AI to read the enhanced versions of your story aloud. You can create custom voice profiles that clone a family member\'s voice, so stories can be narrated in their own voice — even if they\'re no longer with us.'
      },
      {
        q: 'How do I create a Voice Profile?',
        a: 'Go to your Family Circle → Members tab → "Create Voice Profile." You can clone a voice from any existing story recording. The AI needs at least a few seconds of clear speech to create a good voice clone.'
      },
      {
        q: 'Is my data used to train AI models?',
        a: 'No. Your stories, recordings, and voice profiles are never used to train AI models. They are stored securely and only accessible to your family circle members.'
      }
    ]
  },
  {
    title: 'Organization & Export',
    icon: '📚',
    items: [
      {
        q: 'How do categories and tags work?',
        a: 'Each story can have a category (like "Early Life," "Career," "Travel") and multiple tags. Categories organize stories into chapters when you export a memory book. Tags help you find stories by topic. You can use preset categories or create custom ones.'
      },
      {
        q: 'How do I link related stories?',
        a: 'Open any story → scroll to "Linked Stories" → click "+ Link Story." Search by title or filter by storyteller to find the story you want to connect. Choose a relationship type (continuation, same event, response to, etc.) and add an optional note.'
      },
      {
        q: 'How do I export a Memory Book PDF?',
        a: 'Go to your Family Circle → Stories tab → click "Export Memory Book." Choose your options:\n\n• Story version (Literary, Polished, or Original)\n• Filter by storyteller or category\n• Include/exclude photos\n• Group by category with chapter dividers\n\nThe PDF includes a cover page, table of contents, and beautifully formatted stories.'
      },
      {
        q: 'Can I export the Family Tree?',
        a: 'Yes! Go to the Tree tab and click "Export Tree PDF." It generates a visual family tree with member cards showing relationships, birth/death dates, and locations.'
      },
      {
        q: 'How do I download a recording?',
        a: 'Open a story with a recording → the audio player has a three-dot menu with a download option. For original recordings, there\'s also a "Save" button next to the recording label.'
      }
    ]
  },
  {
    title: 'Family Tree',
    icon: '🌳',
    items: [
      {
        q: 'How do I build the Family Tree?',
        a: 'Go to your Family Circle → Tree tab. Members are automatically added when they join. Vault Keepers can click any member to edit their:\n\n• Relationship label (Father, Mother, etc.)\n• Generation level\n• Parent and spouse connections\n• Birth/death dates and places\n• Bio'
      },
      {
        q: 'Can I add people who aren\'t app users?',
        a: 'Currently, tree members are based on circle members. To add someone who isn\'t using the app, invite them as a Listener (they don\'t need to actively use it), then edit their tree info.'
      },
      {
        q: 'What are generations?',
        a: 'Generations organize your tree vertically:\n\n• Grandparents & Above (-2)\n• Parents (-1)\n• You & Siblings (0)\n• Children (1)\n• Grandchildren (2)\n\nThis determines how members appear in the tree layout.'
      }
    ]
  },
  {
    title: 'Privacy & Security',
    icon: '🔒',
    items: [
      {
        q: 'Who can see my stories?',
        a: 'By default, stories are visible to all members of your Family Circle. You can restrict visibility to Vault Keepers only, or select specific members. Time Capsule stories are locked until their unlock date.'
      },
      {
        q: 'What is a Time Capsule?',
        a: 'A Time Capsule locks a story until a future date you choose — perfect for milestone birthdays, graduations, or anniversaries. The story is hidden until the unlock date arrives.'
      },
      {
        q: 'How is my data stored?',
        a: 'All data is stored securely on AWS with encryption at rest and in transit. Audio recordings and photos are stored in private S3 buckets. Only authenticated family circle members can access your content.'
      },
      {
        q: 'Can I delete my account and data?',
        a: 'Yes. Go to Settings → scroll to the bottom → "Delete Account." This permanently removes your account, stories, and all associated data. This action cannot be undone.'
      }
    ]
  },
  {
    title: 'Shared Memories',
    icon: '💌',
    items: [
      {
        q: 'What are Shared Memories?',
        a: "Shared Memories let guests contribute stories, photos, and videos to your family circle without needing an account. You generate a QR code or link for an event — like a birthday, wedding, reunion, or memorial — and guests submit directly from their phone or browser."
      },
      {
        q: 'How do I generate a share link?',
        a: "Open a Family Circle → Memories tab → tap \"Generate Share Link.\" Choose an event type, give it a name, optionally add the honoree's name, set an expiry date, and tap Generate. You'll get a QR code and a shareable URL instantly."
      },
      {
        q: 'Do guests need an account to submit?',
        a: "No. Guests only need their name and optionally their email address. They can submit text, a photo, or a video right from their phone's browser — no download or signup required."
      },
      {
        q: 'How do I review and approve submissions?',
        a: "Go to your Family Circle → Memories tab. Pending submissions appear with an Approve or Hide button. Approved stories are saved permanently to your circle. Rejected stories are automatically removed after 30 days."
      },
      {
        q: 'Do guests get a confirmation after submitting?',
        a: "Yes — if a guest provides their email, they receive a receipt email with their submitted text and a link to view their memory. The link shows their submission with the event theme and a status badge (Pending, Published, or Under Review)."
      },
      {
        q: 'Can I delete an approved memory later?',
        a: "Yes. Vault Keepers can delete any guest memory regardless of status. Open the Memories tab, filter by Approved, and tap the Delete button on the story card."
      },
      {
        q: 'What event types are supported?',
        a: "Birthday, Anniversary, Memorial / Funeral, Graduation, Wedding, Reunion, and Other. Each event type has its own themed landing page with matching colors and copy — so guests see a page styled for the occasion."
      }
    ]
  },
  {
    title: 'Plans & Billing',
    icon: '💳',
    items: [
      {
        q: 'What\'s included in the Free plan?',
        a: 'The Free plan includes 1 Family Circle, up to 3 members, and 5 stories. It\'s a great way to try EchoMemoir and preserve a few important stories.'
      },
      {
        q: 'What do paid plans offer?',
        a: 'The Storyteller plan ($10/month) gives you unlimited stories, up to 10 members, AI voice narration, and PDF exports. The Legacy plan ($30/month) adds unlimited circles, unlimited members, priority AI processing, and premium support.'
      },
      {
        q: 'Can I change my plan anytime?',
        a: 'Yes. Go to Settings → Subscription to upgrade, downgrade, or cancel your plan at any time. Changes take effect at the start of your next billing cycle.'
      }
    ]
  }
];

const TUTORIALS = [
  {
    title: 'Record Your First Story',
    duration: '2 min',
    icon: '🎙️',
    steps: [
      'Go to your Family Circle',
      'Click "Record a Story" in the Stories tab',
      'Choose a prompt or click "Freestyle"',
      'Press the record button and start talking',
      'Press stop when finished',
      'Give your story a title',
      'Click "Enhance with AI" to polish it'
    ]
  },
  {
    title: 'Set Up Your Family Tree',
    duration: '3 min',
    icon: '🌳',
    steps: [
      'Go to the Tree tab in your Family Circle',
      'Click on any member to edit their details',
      'Set their relationship label (Father, Mother, etc.)',
      'Choose their generation level',
      'Connect parents and spouses',
      'Add birth dates, places, and a short bio',
      'Export as PDF to share with family'
    ]
  },
  {
    title: 'Create a Memory Book',
    duration: '2 min',
    icon: '📚',
    steps: [
      'Record and enhance several stories',
      'Add categories and tags to organize them',
      'Go to the Stories tab',
      'Click "Export Memory Book"',
      'Choose your preferred story version',
      'Filter by storyteller or category if desired',
      'Click "Generate PDF" and download your book'
    ]
  },
  {
    title: 'Clone a Voice',
    duration: '3 min',
    icon: '🎤',
    steps: [
      'Record a story with clear audio',
      'Open the story and scroll to the voice section',
      'Click "Clone Voice from This Recording"',
      'Name the voice profile',
      'Wait for processing to complete',
      'Select the cloned voice for AI narration',
      'Generate narration to hear the story in their voice'
    ]
  },
  {
    title: 'Collect Guest Memories',
    duration: '2 min',
    icon: '💌',
    steps: [
      'Open a Family Circle and tap the Memories tab',
      'Tap "Generate Share Link" and choose your event type',
      'Set an event name and optional honoree name',
      'Share the QR code or link with guests',
      'Guests submit text, photos, or videos — no account needed',
      'Review submissions and approve the ones you want to keep',
      'Approved memories are saved permanently to your circle'
    ]
  },
  {
    title: 'Invite Family Members',
    duration: '1 min',
    icon: '👨‍👩‍👧‍👦',
    steps: [
      'Go to your Family Circle',
      'Switch to the Members tab',
      'Click "Invite Member"',
      'Enter their email address',
      'Choose a role (Storyteller, Contributor, or Listener)',
      'Click Send — they\'ll get an email invite',
      'Once accepted, they can start contributing'
    ]
  }
];

const FAQ_SECTIONS_ES = [
  {
    title: 'Primeros Pasos',
    icon: '🚀',
    items: [
      {
        q: '¿Qué es EchoMemoir?',
        a: 'EchoMemoir es una plataforma para preservar historias familiares. Graba historias de tus seres queridos, mejóralas con IA y presérvalas para futuras generaciones. Piensa en ello como un archivo familiar viviente — voces, historias y recuerdos en un solo lugar.'
      },
      {
        q: '¿Cómo creo un Círculo Familiar?',
        a: 'Después de iniciar sesión, haz clic en "Crear Círculo Familiar" en tu panel. Dale un nombre (como "La Familia García") y estarás listo. Luego puedes invitar a miembros de la familia a unirse y comenzar a grabar historias juntos.'
      },
      {
        q: '¿Cómo invito a miembros de la familia?',
        a: 'Ve a tu Círculo Familiar → pestaña Miembros → haz clic en "Invitar Miembro". Ingresa su correo electrónico y elige su rol (Narrador, Colaborador u Oyente). Recibirán una invitación por correo electrónico para unirse.'
      },
      {
        q: '¿Cuáles son los diferentes roles de miembro?',
        a: 'Hay cuatro roles:\n\n• Guardián — Acceso completo de administrador. Puede gestionar miembros, editar el árbol genealógico y controlar todas las configuraciones.\n• Narrador — Puede grabar historias, agregar fotos y comentar historias.\n• Colaborador — Puede agregar comentarios y ver historias. Puede invitar a otros si está habilitado.\n• Oyente — Acceso de solo lectura a las historias compartidas.'
      }
    ]
  },
  {
    title: 'Grabar Historias',
    icon: '🎙️',
    items: [
      {
        q: '¿Cómo grabo una historia?',
        a: 'Ve a tu Círculo Familiar → pestaña Historias → haz clic en "Grabar una Historia". Puedes elegir una pregunta guía para inspirarte o improvisar. Haz clic en el botón de grabar, habla naturalmente y haz clic en detener cuando termines. Tu grabación se transcribe automáticamente.'
      },
      {
        q: '¿Qué son las Preguntas Guía?',
        a: 'Las Preguntas Guía son temas de conversación diseñados para ayudar a desbloquear recuerdos. Cubren temas como infancia, escuela, carrera, tradiciones familiares y más. Explora la biblioteca de preguntas al crear una nueva historia para inspirarte.'
      },
      {
        q: '¿Puedo subir una grabación existente?',
        a: 'Actualmente, las historias se graban directamente en la aplicación. Esto asegura la mejor calidad para la transcripción y mejora con IA. Estamos trabajando en soporte para subir audio en una versión futura.'
      },
      {
        q: '¿Cuánto puede durar una grabación?',
        a: 'No hay un límite de tiempo estricto, pero recomendamos mantener las historias entre 2 y 15 minutos para la mejor experiencia. Las grabaciones más largas funcionan bien pero pueden tardar más en procesarse.'
      }
    ]
  },
  {
    title: 'Funciones de IA',
    icon: '✨',
    items: [
      {
        q: '¿Qué hace "Mejorar con IA"?',
        a: 'La mejora con IA toma tu transcripción original y crea versiones pulidas:\n\n• Pulida — Corrige gramática, elimina muletillas y reestructura para mayor claridad.\n• Literaria — Transforma la historia en una narrativa bellamente escrita preservando la voz y el significado del narrador.'
      },
      {
        q: '¿Qué es la Narración por Voz con IA?',
        a: 'La narración por voz usa IA para leer en voz alta las versiones mejoradas de tu historia. Puedes crear perfiles de voz personalizados que clonan la voz de un familiar, para que las historias se narren con su propia voz — incluso si ya no están con nosotros.'
      },
      {
        q: '¿Cómo creo un Perfil de Voz?',
        a: 'Ve a tu Círculo Familiar → pestaña Miembros → "Crear Perfil de Voz". Puedes clonar una voz de cualquier grabación de historia existente. La IA necesita al menos unos segundos de habla clara para crear un buen clon de voz.'
      },
      {
        q: '¿Se usan mis datos para entrenar modelos de IA?',
        a: 'No. Tus historias, grabaciones y perfiles de voz nunca se usan para entrenar modelos de IA. Se almacenan de forma segura y solo son accesibles para los miembros de tu círculo familiar.'
      }
    ]
  },
  {
    title: 'Organización y Exportación',
    icon: '📚',
    items: [
      {
        q: '¿Cómo funcionan las categorías y etiquetas?',
        a: 'Cada historia puede tener una categoría (como "Vida Temprana", "Carrera", "Viajes") y múltiples etiquetas. Las categorías organizan las historias en capítulos cuando exportas un libro de recuerdos. Las etiquetas te ayudan a encontrar historias por tema.'
      },
      {
        q: '¿Cómo vinculo historias relacionadas?',
        a: 'Abre cualquier historia → desplázate a "Historias Vinculadas" → haz clic en "+ Vincular Historia". Busca por título o filtra por narrador para encontrar la historia que quieres conectar. Elige un tipo de relación (continuación, mismo evento, respuesta a, etc.).'
      },
      {
        q: '¿Cómo exporto un Libro de Recuerdos en PDF?',
        a: 'Ve a tu Círculo Familiar → pestaña Historias → haz clic en "Exportar Libro de Recuerdos". Elige tus opciones:\n\n• Versión de historia (Literaria, Pulida u Original)\n• Filtrar por narrador o categoría\n• Incluir/excluir fotos\n• Agrupar por categoría con divisores de capítulos\n\nEl PDF incluye una portada, tabla de contenidos e historias bellamente formateadas.'
      },
      {
        q: '¿Puedo exportar el Árbol Genealógico?',
        a: '¡Sí! Ve a la pestaña Árbol y haz clic en "Exportar Árbol PDF". Genera un árbol genealógico visual con tarjetas de miembros mostrando relaciones, fechas de nacimiento/fallecimiento y lugares.'
      },
      {
        q: '¿Cómo descargo una grabación?',
        a: 'Abre una historia con grabación → el reproductor de audio tiene un menú de tres puntos con una opción de descarga.'
      }
    ]
  },
  {
    title: 'Árbol Genealógico',
    icon: '🌳',
    items: [
      {
        q: '¿Cómo construyo el Árbol Genealógico?',
        a: 'Ve a tu Círculo Familiar → pestaña Árbol. Los miembros se agregan automáticamente cuando se unen. Los Guardianes pueden hacer clic en cualquier miembro para editar:\n\n• Etiqueta de relación (Padre, Madre, etc.)\n• Nivel de generación\n• Conexiones de padres y cónyuges\n• Fechas y lugares de nacimiento/fallecimiento\n• Biografía'
      },
      {
        q: '¿Puedo agregar personas que no usan la aplicación?',
        a: 'Actualmente, los miembros del árbol se basan en los miembros del círculo. Para agregar a alguien que no usa la aplicación, invítalos como Oyente (no necesitan usarla activamente), luego edita su información del árbol.'
      },
      {
        q: '¿Qué son las generaciones?',
        a: 'Las generaciones organizan tu árbol verticalmente:\n\n• Abuelos y Anteriores (-2)\n• Padres (-1)\n• Tú y Hermanos (0)\n• Hijos (1)\n• Nietos (2)\n\nEsto determina cómo aparecen los miembros en el diseño del árbol.'
      }
    ]
  },
  {
    title: 'Privacidad y Seguridad',
    icon: '🔒',
    items: [
      {
        q: '¿Quién puede ver mis historias?',
        a: 'Por defecto, las historias son visibles para todos los miembros de tu Círculo Familiar. Puedes restringir la visibilidad solo a Guardianes, o seleccionar miembros específicos. Las historias de Cápsula del Tiempo están bloqueadas hasta su fecha de apertura.'
      },
      {
        q: '¿Qué es una Cápsula del Tiempo?',
        a: 'Una Cápsula del Tiempo bloquea una historia hasta una fecha futura que elijas — perfecto para cumpleaños importantes, graduaciones o aniversarios. La historia permanece oculta hasta que llega la fecha de apertura.'
      },
      {
        q: '¿Cómo se almacenan mis datos?',
        a: 'Todos los datos se almacenan de forma segura en AWS con cifrado en reposo y en tránsito. Las grabaciones de audio y fotos se almacenan en buckets privados de S3. Solo los miembros autenticados del círculo familiar pueden acceder a tu contenido.'
      },
      {
        q: '¿Puedo eliminar mi cuenta y datos?',
        a: 'Sí. Ve a Configuración → desplázate hasta abajo → "Eliminar Cuenta". Esto elimina permanentemente tu cuenta, historias y todos los datos asociados. Esta acción no se puede deshacer.'
      }
    ]
  },
  {
    title: 'Recuerdos Compartidos',
    icon: '💌',
    items: [
      {
        q: '¿Qué son los Recuerdos Compartidos?',
        a: 'Los Recuerdos Compartidos permiten que los invitados contribuyan historias, fotos y videos a tu círculo familiar sin necesitar una cuenta. Generas un código QR o enlace para un evento — como un cumpleaños, boda, reunión o memorial — y los invitados envían directamente desde su teléfono o navegador.'
      },
      {
        q: '¿Cómo genero un enlace para compartir?',
        a: 'Abre un Círculo Familiar → pestaña Recuerdos → toca "Generar Enlace". Elige el tipo de evento, dale un nombre, opcionalmente agrega el nombre del homenajeado, establece una fecha de vencimiento y toca Generar. Obtendrás un código QR y una URL compartible al instante.'
      },
      {
        q: '¿Los invitados necesitan una cuenta para enviar?',
        a: 'No. Los invitados solo necesitan su nombre y opcionalmente su correo electrónico. Pueden enviar texto, una foto o un video directamente desde el navegador de su teléfono — sin descargas ni registro requerido.'
      },
      {
        q: '¿Cómo reviso y apruebo las entregas?',
        a: 'Ve a tu Círculo Familiar → pestaña Recuerdos. Las entregas pendientes aparecen con un botón de Aprobar u Ocultar. Las historias aprobadas se guardan permanentemente en tu círculo. Las historias rechazadas se eliminan automáticamente después de 30 días.'
      },
      {
        q: '¿Los invitados reciben una confirmación después de enviar?',
        a: 'Sí — si un invitado proporciona su correo, recibe un email de confirmación con su texto enviado y un enlace para ver su recuerdo. El enlace muestra su envío con el tema del evento y una insignia de estado (Pendiente, Publicado o En Revisión).'
      },
      {
        q: '¿Puedo eliminar un recuerdo aprobado después?',
        a: 'Sí. Los Guardianes pueden eliminar cualquier recuerdo de invitado independientemente de su estado. Abre la pestaña Recuerdos, filtra por Aprobados y toca el botón Eliminar en la tarjeta de la historia.'
      },
      {
        q: '¿Qué tipos de eventos son compatibles?',
        a: 'Cumpleaños, Aniversario, Memorial / Funeral, Graduación, Boda, Reunión y Otro. Cada tipo de evento tiene su propia página de destino temática con colores y texto acordes — para que los invitados vean una página diseñada para la ocasión.'
      }
    ]
  },
  {
    title: 'Planes y Facturación',
    icon: '💳',
    items: [
      {
        q: '¿Qué incluye el plan Gratuito?',
        a: 'El plan Gratuito incluye 1 Círculo Familiar, hasta 3 miembros y 5 historias. Es una excelente manera de probar EchoMemoir y preservar algunas historias importantes.'
      },
      {
        q: '¿Qué ofrecen los planes de pago?',
        a: 'El plan Narrador ($10/mes) te da historias ilimitadas, hasta 10 miembros, narración por voz con IA y exportaciones PDF. El plan Legado ($30/mes) agrega círculos ilimitados, miembros ilimitados, procesamiento de IA prioritario y soporte premium.'
      },
      {
        q: '¿Puedo cambiar mi plan en cualquier momento?',
        a: 'Sí. Ve a Configuración → Suscripción para mejorar, reducir o cancelar tu plan en cualquier momento. Los cambios toman efecto al inicio de tu próximo ciclo de facturación.'
      }
    ]
  }
];

const TUTORIALS_ES = [
  {
    title: 'Graba Tu Primera Historia',
    duration: '2 min',
    icon: '🎙️',
    steps: [
      'Ve a tu Círculo Familiar',
      'Haz clic en "Grabar una Historia" en la pestaña Historias',
      'Elige una pregunta guía o haz clic en "Libre"',
      'Presiona el botón de grabar y comienza a hablar',
      'Presiona detener cuando termines',
      'Dale un título a tu historia',
      'Haz clic en "Mejorar con IA" para pulirla'
    ]
  },
  {
    title: 'Configura Tu Árbol Genealógico',
    duration: '3 min',
    icon: '🌳',
    steps: [
      'Ve a la pestaña Árbol en tu Círculo Familiar',
      'Haz clic en cualquier miembro para editar sus detalles',
      'Establece su etiqueta de relación (Padre, Madre, etc.)',
      'Elige su nivel de generación',
      'Conecta padres y cónyuges',
      'Agrega fechas de nacimiento, lugares y una biografía corta',
      'Exporta como PDF para compartir con la familia'
    ]
  },
  {
    title: 'Crea un Libro de Recuerdos',
    duration: '2 min',
    icon: '📚',
    steps: [
      'Graba y mejora varias historias',
      'Agrega categorías y etiquetas para organizarlas',
      'Ve a la pestaña Historias',
      'Haz clic en "Exportar Libro de Recuerdos"',
      'Elige tu versión de historia preferida',
      'Filtra por narrador o categoría si lo deseas',
      'Haz clic en "Generar PDF" y descarga tu libro'
    ]
  },
  {
    title: 'Clona una Voz',
    duration: '3 min',
    icon: '🎤',
    steps: [
      'Graba una historia con audio claro',
      'Abre la historia y desplázate a la sección de voz',
      'Haz clic en "Clonar Voz de Esta Grabación"',
      'Nombra el perfil de voz',
      'Espera a que se complete el procesamiento',
      'Selecciona la voz clonada para narración con IA',
      'Genera la narración para escuchar la historia con su voz'
    ]
  },
  {
    title: 'Recopila Recuerdos de Invitados',
    duration: '2 min',
    icon: '💌',
    steps: [
      'Abre un Círculo Familiar y toca la pestaña Recuerdos',
      'Toca "Generar Enlace" y elige el tipo de evento',
      'Establece un nombre de evento y el nombre del homenajeado (opcional)',
      'Comparte el código QR o enlace con los invitados',
      'Los invitados envían texto, fotos o videos — sin necesidad de cuenta',
      'Revisa las entregas y aprueba las que quieres guardar',
      'Los recuerdos aprobados se guardan permanentemente en tu círculo'
    ]
  },
  {
    title: 'Invita a Miembros de la Familia',
    duration: '1 min',
    icon: '👨‍👩‍👧‍👦',
    steps: [
      'Ve a tu Círculo Familiar',
      'Cambia a la pestaña Miembros',
      'Haz clic en "Invitar Miembro"',
      'Ingresa su correo electrónico',
      'Elige un rol (Narrador, Colaborador u Oyente)',
      'Haz clic en Enviar — recibirán una invitación por correo',
      'Una vez aceptada, pueden comenzar a contribuir'
    ]
  }
];

function Help() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTutorial, setActiveTutorial] = useState(null);

  const isEs = language === 'es';
  const faqSections = isEs ? FAQ_SECTIONS_ES : FAQ_SECTIONS;
  const tutorials = isEs ? TUTORIALS_ES : TUTORIALS;

  const toggleFAQ = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setOpenFAQ(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter FAQ items by search
  const filteredSections = searchQuery.trim()
    ? faqSections.map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.items.length > 0)
    : (activeSection !== null ? [faqSections[activeSection]] : faqSections);

  return (
    <div className="help-page">
      <header className="help-header">
        <div className="help-header-top">
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← {isEs ? 'Volver' : 'Back'}
          </button>
          {user && (
            <button className="btn-back" onClick={() => navigate('/')}>
              🏠 Dashboard
            </button>
          )}
        </div>
        <h1>{isEs ? 'Centro de Ayuda' : 'Help Center'}</h1>
        <p className="help-subtitle">{isEs ? 'Todo lo que necesitas saber sobre EchoMemoir' : 'Everything you need to know about EchoMemoir'}</p>
        
        <div className="help-search">
          <input
            type="text"
            placeholder={isEs ? 'Buscar ayuda...' : 'Search for help...'}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setActiveSection(null); }}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      </header>

      <main className="help-main">
        {/* Quick Start Tutorials */}
        {!searchQuery && (
          <section className="help-section tutorials-section">
            <h2>📖 {isEs ? 'Guías de Inicio Rápido' : 'Quick Start Guides'}</h2>
            <div className="tutorials-grid">
              {tutorials.map((tutorial, idx) => (
                <div 
                  key={idx} 
                  className={`tutorial-card ${activeTutorial === idx ? 'active' : ''}`}
                  onClick={() => setActiveTutorial(activeTutorial === idx ? null : idx)}
                >
                  <div className="tutorial-card-header">
                    <span className="tutorial-icon">{tutorial.icon}</span>
                    <div>
                      <h3>{tutorial.title}</h3>
                      <span className="tutorial-duration">{tutorial.duration}</span>
                    </div>
                  </div>
                  {activeTutorial === idx && (
                    <div className="tutorial-steps">
                      {tutorial.steps.map((step, sIdx) => (
                        <div key={sIdx} className="tutorial-step">
                          <span className="step-number">{sIdx + 1}</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Category Pills */}
        {!searchQuery && (
          <div className="faq-category-pills">
            <button 
              className={`pill ${activeSection === null ? 'active' : ''}`}
              onClick={() => setActiveSection(null)}
            >
              {isEs ? 'Todos los Temas' : 'All Topics'}
            </button>
            {faqSections.map((section, idx) => (
              <button 
                key={idx}
                className={`pill ${activeSection === idx ? 'active' : ''}`}
                onClick={() => setActiveSection(idx)}
              >
                {section.icon} {section.title}
              </button>
            ))}
          </div>
        )}

        {/* FAQ Sections */}
        <section className="help-section faq-section">
          <h2>{searchQuery ? (isEs ? 'Resultados de Búsqueda' : 'Search Results') : (isEs ? '❓ Preguntas Frecuentes' : '❓ Frequently Asked Questions')}</h2>
          
          {filteredSections.length === 0 && (
            <div className="no-results">
              <p>{isEs ? `No se encontraron resultados para "${searchQuery}"` : `No results found for "${searchQuery}"`}</p>
              <button className="btn-secondary" onClick={() => setSearchQuery('')}>
                {isEs ? 'Borrar búsqueda' : 'Clear search'}
              </button>
            </div>
          )}

          {filteredSections.map((section, sIdx) => {
            const actualSIdx = faqSections.indexOf(section);
            return (
              <div key={sIdx} className="faq-group">
                {(activeSection === null || searchQuery) && (
                  <h3 className="faq-group-title">
                    {section.icon} {section.title}
                  </h3>
                )}
                {section.items.map((item, iIdx) => {
                  const key = `${actualSIdx}-${iIdx}`;
                  const isOpen = openFAQ[key] || !!searchQuery;
                  return (
                    <div key={iIdx} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button 
                        className="faq-question"
                        onClick={() => toggleFAQ(actualSIdx, iIdx)}
                      >
                        <span>{item.q}</span>
                        <span className="faq-toggle">{isOpen ? '−' : '+'}</span>
                      </button>
                      {isOpen && (
                        <div className="faq-answer">
                          {item.a.split('\n\n').map((para, pIdx) => (
                            <p key={pIdx}>
                              {para.split('\n').map((line, lIdx) => (
                                <React.Fragment key={lIdx}>
                                  {lIdx > 0 && <br />}
                                  {line}
                                </React.Fragment>
                              ))}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>

        {/* Contact */}
        <section className="help-section contact-section">
          <h2>💬 {isEs ? '¿Aún Necesitas Ayuda?' : 'Still Need Help?'}</h2>
          <p>{isEs ? '¿No encuentras lo que buscas? Contáctanos.' : "Can't find what you're looking for? Reach out to our team."}</p>
          <a href="mailto:support@echomemoir.ai" className="btn-primary contact-btn">
            {isEs ? 'Correo de Soporte' : 'Email Support'}
          </a>
        </section>
      </main>

      <footer className="help-footer">
        <p>© {new Date().getFullYear()} EchoMemoir · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></p>
      </footer>
    </div>
  );
}

export default Help;